// ============================================================
// OverviewPage — KPI temps réel via Socket.IO + REST
// ============================================================

import { useEffect, useState } from 'react';
import { api } from '../api';
import { useSocket } from '../hooks/useSocket';
import type { Community, User, AuditLog } from '@siverix/shared';

interface Counters {
  users: number;
  communities: number;
  events: number;
}

export function OverviewPage(): JSX.Element {
  const [counts, setCounts] = useState<Counters>({ users: 0, communities: 0, events: 0 });
  const [recent, setRecent] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const { socket } = useSocket();

  async function fetchAll(): Promise<void> {
    try {
      const [users, communities, audit] = await Promise.all([
        api.adminListUsers(),
        api.adminListCommunities(),
        api.adminAuditLog(),
      ]);
      setCounts({ users: users.length, communities: communities.length, events: audit.length });
      setRecent(audit.slice(0, 8));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchAll();
  }, []);

  // Live: rafraîchit dès qu'un audit log arrive
  useEffect(() => {
    if (!socket) return;
    const handler = (): void => void fetchAll();
    socket.on('audit:new', handler);
    socket.on('user:created', handler);
    socket.on('community:created', handler);
    return () => {
      socket.off('audit:new', handler);
      socket.off('user:created', handler);
      socket.off('community:created', handler);
    };
  }, [socket]);

  if (loading) return <div>Chargement…</div>;
  if (err) return <ErrorBox message={err} />;

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Vue d'ensemble</h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
        État de l'instance Siverix en temps réel.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px',
          margin: '20px 0',
        }}
      >
        <KpiCard label="Utilisateurs" value={counts.users} />
        <KpiCard label="Communautés" value={counts.communities} />
        <KpiCard label="Événements audit" value={counts.events} />
      </div>

      <h2 style={{ fontSize: '16px', marginTop: '32px' }}>Activité récente</h2>
      <div
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {recent.length === 0 ? (
          <div style={{ padding: '24px', color: 'var(--color-text-muted)', fontSize: '13px' }}>
            Aucune activité enregistrée.
          </div>
        ) : (
          recent.map((e) => (
            <div
              key={e.id}
              style={{
                padding: '10px 16px',
                borderBottom: '1px solid var(--color-border)',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px',
              }}
            >
              <span>
                <code>{e.action}</code> {e.targetType ? `→ ${e.targetType}` : ''}
              </span>
              <span style={{ color: 'var(--color-text-muted)' }}>
                {new Date(e.createdAt).toLocaleString('fr-FR')}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: '10px',
        padding: '20px',
      }}
    >
      <div
        style={{
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '.05em',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: '32px', fontWeight: 700, marginTop: '6px' }}>{value}</div>
    </div>
  );
}

export function ErrorBox({ message }: { message: string }): JSX.Element {
  return (
    <div
      style={{
        padding: '12px 16px',
        background: 'rgba(248,81,73,.15)',
        color: 'var(--color-error)',
        borderRadius: '6px',
        fontSize: '13px',
      }}
    >
      {message}
    </div>
  );
}

// alias pour réutilisation dans d'autres pages
export type { User, Community };
